import { useLocation, useParams } from "react-router";
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import { datasetLabelArr } from "src/business-utils/dataset/dataset.utils.ts";
import { getMetadataByAssetType } from "src/components/resources/AssetNameWithIcon/AssetNameWithIcon.utils";
import { useBuildLink } from "src/hooks/useBuildLink";
import { useQueryTask } from "src/queries/tasks/tasks.query";
import { useQueryTransformation } from "src/queries/tfs/tfs.query";
import { appRoutes, getRouteObject } from "src/utils/appRoutes/appRoutes";
import type { DatasetPath } from "src/utils/appRoutes/appRoutes.types";
import { TransformationIcon } from "@ui-icons";
import { breadCrumbsStyles } from "./Breadcrumbs.constants";
import BreadcrumbClusterLink from "./components/BreadcrumbClusterLink/BreadcrumbClusterLink";
import BreadcrumbLink from "./components/BreadcrumbLink/BreadcrumbLink";
import BreadcrumbPipelineLink from "./components/BreadcrumbPipelineLink/BreadcrumbPipelineLink";
import BreadcrumbPitLink from "./components/BreadcrumbPitLink/BreadcrumbPitLink";
import BreadcrumbRunTimeLink from "./components/BreadcrumbRunTimeLink/BreadcrumbRunTimeLink";
import BreadcrumbTaskLink from "./components/BreadcrumbTaskLink/BreadcrumbTaskLink";
import BreadcrumbTaskRunsLink from "./components/BreadcrumbTaskRunsLink/BreadcrumbTaskRunsLink";

export const Breadcrumbs = () => {
  const params = useParams();
  const {
    env_name: env,
    app_name,
    cluster_name,
    app_pit,
    task_name,
    task_id,
    tf_id,
    ds_name,
  } = params;

  const taskQuery = useQueryTask({
    params: {
      task_id,
    },
    caller: "Breadcrumbs",
  });

  const transformationQuery = useQueryTransformation();

  const { pathname } = useLocation();

  // TODO move to buildLink
  const routesNames = ["compute", "apps", "runs", "datasets", "alerts"] as const;
  const [
    baseComputePath,
    baseAppsPath,
    baseRunsPath,
    baseDatasetsPath,
    baseAlertsPath,
  ] = routesNames.map((name) => {
    const route = appRoutes[name];
    return route?.matcher(pathname) && env ? route.getBasePath(env) : undefined;
  });

  // get the dataset-type icon
  const labelArr = datasetLabelArr(ds_name || "");
  const datasetType = labelArr[1] === "file" ? "file" : "table";
  const assetData = getMetadataByAssetType(datasetType);
  const DatasetIcon = assetData?.Icon;

  const buildLink = useBuildLink();

  if (!getRouteObject(pathname)?.hasBreadcrumbs) {
    return "";
  }
  return (
    <div
      className="flex gap-1 items-center py-2"
      style={{ container: "breadcrumbs / size" }}
    >
      <MuiBreadcrumbs
        className="inline-flex w-fit max-w-none min-w-0"
        aria-label="breadcrumb"
        sx={breadCrumbsStyles}
      >
        {/* Compute-related breadcrumbs */}
        {baseComputePath && (
          <BreadcrumbLink to={baseComputePath}>Compute</BreadcrumbLink>
        )}

        {/* Pipelines-related breadcrumbs */}
        {baseAppsPath && (
          <BreadcrumbLink to={baseAppsPath}>Pipelines</BreadcrumbLink>
        )}
        {env && app_name && !cluster_name && (
          <BreadcrumbPipelineLink env={env} app_name={app_name} />
        )}
        {baseComputePath && app_name && cluster_name && (
          <BreadcrumbClusterLink app_name={app_name} cluster_name={cluster_name} />
        )}
        {baseComputePath && app_name && <BreadcrumbRunTimeLink />}

        {baseRunsPath && <BreadcrumbLink to={baseRunsPath}>Runs</BreadcrumbLink>}
        {baseDatasetsPath && (
          <BreadcrumbLink to={baseDatasetsPath}>Datasets</BreadcrumbLink>
        )}
        {ds_name && (
          <BreadcrumbLink
            to={buildLink({ dataset: ds_name } as DatasetPath) as string}
            startAdornment={DatasetIcon}
          >
            <span className="align-middle">{ds_name}</span>
          </BreadcrumbLink>
        )}
        {baseAlertsPath && (
          <BreadcrumbLink to={baseAlertsPath}>Alerts</BreadcrumbLink>
        )}

        {env && app_name && !baseComputePath && (
          <BreadcrumbTaskLink
            app_name={app_name}
            app_pit={app_pit}
            task_name={task_name || taskQuery?.data?.task_name}
            isLoading={taskQuery.isFetching}
          />
        )}

        {false && env && app_name && app_pit && !baseComputePath && (
          // Use task name instead of id
          <BreadcrumbTaskRunsLink
            taskQuery={taskQuery}
            env={env}
            app_name={app_name}
            app_pit={app_pit}
            task_id={task_id}
          />
        )}

        {app_name && !baseComputePath && (
          <BreadcrumbPitLink
            task_name={task_name || taskQuery?.data?.task_name}
            isLoading={taskQuery.isFetching}
            {...{ env, app_name, app_pit }}
          />
        )}

        {tf_id && transformationQuery && (
          <BreadcrumbLink
            isLoading={transformationQuery.isPending}
            to={`/envs/${env}/apps/${app_name}/${app_pit}/${task_id}/${tf_id}`}
            startAdornment={TransformationIcon}
            tooltip={transformationQuery.data?.output_name}
            data-test-id="breadcrumb-tf"
          >
            {`TF${transformationQuery.data?.agent_tf_id}`}
            <span data-extra>
              {transformationQuery.data?.output_name
                ? `: ${transformationQuery.data?.output_name}`
                : ""}
            </span>
          </BreadcrumbLink>
        )}
      </MuiBreadcrumbs>
    </div>
  );
};
